import React, { useMemo, useState } from 'react'

import { Collapse, Space, Typography } from 'antd'
import { BaseType } from 'antd/lib/typography/Base'
import { get } from 'lodash'
import { MetricsChart, QueryConfig, TransformNullValue } from 'metrics-chart'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { ClusterCluster } from '@clinic/client'
import { grafanaQueryRange } from '~/api'
import { useCurOrg } from '~/hooks/state'
import { benchmarkReportTelemetry } from '~/telemetry/benchmark-report'
import { RemoteNamespace } from '~/translations'

import {
  Advice,
  AdviceLevel,
  MetricsChartQueryConfig,
  ReportArgs
} from '../../shared/types'
import { replaceQuery } from '../../shared/utils/promql'
import { unitMap } from '../../shared/utils/unit'
import { BENCHMARK_REPORT_NS } from '../translation'
import { BenchmarkReportChart, BenchmarkReportMetricCategory } from '../types'
import { GrafanaLink, MetricDashboard } from './GrafanaLink'
import type { PromqlSchemaMap, MetricStatsMap } from './index'

import '../translation'

import '@elastic/charts/dist/theme_only_light.css'
import './index.css'

const { Text, Link } = Typography

const toMetricChartSchema = (
  schema: MetricsChartQueryConfig[],
  promqlSchema: PromqlSchemaMap
): QueryConfig[] => {
  return schema
    .map((q) => {
      const promqlData = promqlSchema[q.promqlId]
      if (!promqlData) {
        console.error(`\`${q.promqlId}\` not exists.`)
        return null
      }
      return {
        ...q,
        dashboard: promqlData.dashboard,
        promql: promqlData.expr
      }
    })
    .filter((s) => !!s) as any
}

interface MetricCategoryProps {
  cate: BenchmarkReportMetricCategory
  promqlSchema: PromqlSchemaMap
  advices: Advice[]
  metricStats: MetricStatsMap
  reportArgs: ReportArgs
  cluster?: ClusterCluster
}

const transformMetricValues = (v?: string | string[]) => {
  if (!v) {
    return 'N/A'
  }
  if (typeof v === 'string') {
    return v
  }
  if (Array.isArray(v)) {
    return `${v[0]}[${v[1]}]`
  }
  return 'Unknwon'
}

export const MetricCategory: React.FC<MetricCategoryProps> = ({
  cate,
  promqlSchema,
  advices = [],
  metricStats,
  reportArgs,
  cluster
}) => {
  const { t } = useTranslation(BENCHMARK_REPORT_NS)
  const { orgId, cid } = useParams<'orgId' | 'cid' | 'rid'>()
  const { curOrg } = useCurOrg()
  const { isCloud, orgType } = curOrg!

  const [showDetails, setShowDetails] = useState(false)
  const errorAdvices = advices.filter((ad) => ad.level === AdviceLevel.Error)
  const warningAdvices = advices.filter(
    (ad) => ad.level === AdviceLevel.Warning
  )

  const toggleDetails = () =>
    setShowDetails((v) => {
      benchmarkReportTelemetry.toggleMainMetric(cate.name, !v)
      return !v
    })

  const ToggleAction = () => (
    <a onClick={toggleDetails}>{showDetails ? t('hide') : t('more')}</a>
  )

  return (
    <>
      <div className='cate-header flex border border-solid border-[#ccc] border-t-0'>
        <div className='flex basis-40 shrink-0 p-2 content-center border-y-0 border-l-0 border-r border-solid border-[#ccc]'>
          <h3 className='mb-0'>{cate.name}</h3>
        </div>
        <div className='flex flex-col grow p-2 border-y-0 border-l-0 border-r border-solid border-[#ccc]'>
          <div className='text-[#888]'>
            {cate.mainMetrics.map((metric) => {
              if (typeof metric !== 'string') {
                return (
                  <p className='mb-0 break-all' key={metric.name}>
                    <b>{metric.name}</b>:{' '}
                    {metric.kvs.map((kv, index) => {
                      const [kk, vv] = kv.value.split('#')
                      const values = metricStats[kk]?.values

                      if (!vv) {
                        return Object.entries(values).map(
                          ([objKey, objValue]) => {
                            return (
                              <span key={objKey}>
                                {objKey}({JSON.stringify(objValue)})
                              </span>
                            )
                          }
                        )
                      }

                      const value = get(values, vv, 'N/A')
                      return (
                        <span key={kv.value}>
                          {kv.key}(
                          {typeof value === 'string'
                            ? value
                            : JSON.stringify(value)}
                          ){index === metric.kvs.length - 1 ? '' : ', '}
                        </span>
                      )
                    })}
                  </p>
                )
              }
              const values = metricStats[metric]?.values
              return (
                <p className='mb-0' key={metric}>
                  <b>{metric.split(':')[0]}</b>: Mean(
                  {transformMetricValues(values?.mean)}){', '}
                  Min({transformMetricValues(values?.min)}){', '}
                  Max({transformMetricValues(values?.max)})
                </p>
              )
            })}
          </div>
        </div>
        <div className='flex basis-48 shrink-0 p-2 content-center border-y-0 border-l-0 border-r border-solid border-[#ccc]'>
          <Space>
            {!!warningAdvices.length && (
              <Text
                className='cursor-pointer'
                type='warning'
                onClick={toggleDetails}
              >
                {warningAdvices.length} Warning
              </Text>
            )}
            {!!errorAdvices.length && (
              <Text
                className='cursor-pointer'
                type='danger'
                onClick={toggleDetails}
              >
                {errorAdvices.length} Error
              </Text>
            )}
            {!warningAdvices.length && !errorAdvices.length && (
              <Text type='success'>{t('tips.no_issue')}</Text>
            )}
          </Space>
        </div>
        <div className='flex content-center items-center basis-20 shrink-0 p-2'>
          {(!!advices.length || !!cate.charts.length) && <ToggleAction />}
        </div>
      </div>

      {showDetails && (
        <div className='cate-chart bg-[#fafafa] p-4 my-4'>
          <SingleOrMultiChart
            charts={cate.charts}
            render={(chart) => {
              const queries = toMetricChartSchema(chart.queries, promqlSchema)
              return (
                <div>
                  <h4 className='mb-0 ml-6'>
                    {chart.name}{' '}
                    <GrafanaLink
                      dashboards={queries as MetricDashboard[]}
                      orgId={curOrg?.id!}
                      orgType={curOrg?.orgType!}
                      clusterId={cluster?.id!}
                      reportArgs={reportArgs}
                    />
                  </h4>
                  <MetricsChart
                    unit={chart.unit}
                    queries={queries}
                    range={[reportArgs.begin_ts, reportArgs.end_ts]}
                    nullValue={TransformNullValue.AS_ZERO}
                    fetchPromeData={({
                      endTimeSec,
                      query,
                      startTimeSec,
                      stepSec
                    }) => {
                      return grafanaQueryRange({
                        query: replaceQuery(
                          query,
                          cid!,
                          orgType,
                          cluster?.deployType
                        ),
                        orgId: orgId!,
                        clusterId: cid!,
                        start: startTimeSec,
                        end: endTimeSec,
                        step: stepSec.toString() + 's',
                        isCloud
                      }).then((res) => res.data)
                    }}
                  />
                </div>
              )
            }}
          />
          {(!!warningAdvices.length || !!warningAdvices.length) && (
            <AdvicePanel
              warningAdvices={warningAdvices}
              errorAdvices={errorAdvices}
              promqlSchema={promqlSchema}
              reportArgs={reportArgs}
              cluster={cluster}
            />
          )}
          {!!cate.relatedCharts?.length && (
            <RelatedMetrics
              name={cate.name}
              charts={cate.relatedCharts}
              promqlSchema={promqlSchema}
              reportArgs={reportArgs}
              cluster={cluster}
            />
          )}
        </div>
      )}
    </>
  )
}

interface AdvicePanelProps {
  warningAdvices?: Advice[]
  errorAdvices?: Advice[]
  promqlSchema: PromqlSchemaMap
  reportArgs: ReportArgs
  cluster?: ClusterCluster
}

const adviceLevelMap: { [k: string]: BaseType } = {
  warning: 'warning',
  error: 'danger'
}

export const AdvicePanel: React.FC<AdvicePanelProps> = ({
  warningAdvices,
  errorAdvices,
  promqlSchema,
  reportArgs,
  cluster
}) => {
  const advices = [...(errorAdvices || []), ...(warningAdvices || [])]

  return (
    <table className='border-collapse w-full mt-6'>
      <tbody>
        {advices.map((ad) => {
          return (
            <AdviceRow
              key={ad.id}
              advice={ad}
              promqlSchema={promqlSchema}
              reportArgs={reportArgs}
              cluster={cluster}
            />
          )
        })}
      </tbody>
    </table>
  )
}

interface AdviceRowProps {
  advice: Advice
  promqlSchema: PromqlSchemaMap
  reportArgs: ReportArgs
  cluster?: ClusterCluster
}

const AdviceRow: React.FC<AdviceRowProps> = ({
  advice,
  promqlSchema,
  reportArgs,
  cluster
}) => {
  const { t } = useTranslation(RemoteNamespace.Metrics)
  const { t: tLocal } = useTranslation(BENCHMARK_REPORT_NS)
  const { orgId, cid } = useParams<'orgId' | 'cid' | 'rid'>()

  const { curOrg } = useCurOrg()
  const { orgType, isCloud } = curOrg!

  const [showDetails, setShowDetails] = useState(false)
  const adviceCharts = useMemo(
    () =>
      advice.metrics
        .map((mId) => {
          const promSchema = promqlSchema[mId]
          if (!promSchema) {
            console.error(`\`${mId}\` not exists.`)
            return null
          }
          const unit = unitMap[promSchema?.unit]
          return {
            name: mId,
            unit,
            queries: [
              {
                name: '{name}',
                type: 'line',
                promqlId: mId,
                dashboard: promSchema?.dashboard
              }
            ]
          }
        })
        .filter((c) => !!c) as BenchmarkReportChart[],
    [advice, promqlSchema]
  )

  const ToggleAction = ({ disabled }: { disabled?: boolean }) => (
    <Typography.Link
      disabled={disabled}
      onClick={() =>
        setShowDetails((v) => {
          benchmarkReportTelemetry.toggleAdvice(advice.id, advice.name, !v)
          return !v
        })
      }
    >
      {showDetails ? tLocal('hide') : tLocal('more')}
    </Typography.Link>
  )

  return (
    <>
      <tr>
        <td className='px-2 py-1 border-r border-solid border-[#ccc]'>
          <Text type={adviceLevelMap[advice.level]}>{advice.level}</Text>
        </td>
        <td className='px-2 py-1 border-r border-solid border-[#ccc]'>
          <div className='flex flex-col'>
            <div>Name: {advice.name}</div>
            <div>
              Fact: {t(`${advice.content.key}.fact`, advice.content.variables)}
            </div>
            <div>
              Suggestion:{' '}
              {t(`${advice.content.key}.suggestion`, advice.content.variables)}{' '}
              {!!t(`${advice.content.key}.link`) && (
                <Link
                  onClick={() =>
                    window.open(t(`${advice.content.key}.link`), '_blank')
                  }
                >
                  [Link]
                </Link>
              )}
            </div>
          </div>
        </td>
        <td className='px-2 py-1 border-r border-solid border-[#ccc] min-w-[50px]'>
          <ToggleAction disabled={!adviceCharts.length} />
        </td>
      </tr>
      {showDetails && (
        <tr>
          <td
            colSpan={3}
            className='px-2 pb-1 pt-4 border-r border-solid border-[#ccc]'
          >
            <SingleOrMultiChart
              charts={adviceCharts}
              render={(chart) => (
                <div>
                  <h4 className='mb-0 ml-6'>
                    {chart.name}{' '}
                    <GrafanaLink
                      dashboards={chart.queries as any}
                      orgId={curOrg?.id!}
                      orgType={curOrg?.orgType!}
                      clusterId={cluster?.id!}
                      reportArgs={reportArgs}
                    />
                  </h4>
                  <MetricsChart
                    unit={chart.unit}
                    nullValue={TransformNullValue.AS_ZERO}
                    queries={toMetricChartSchema(chart.queries, promqlSchema)}
                    range={[reportArgs.begin_ts, reportArgs.end_ts]}
                    fetchPromeData={({
                      endTimeSec,
                      query,
                      startTimeSec,
                      stepSec
                    }) => {
                      return grafanaQueryRange({
                        query: replaceQuery(
                          query,
                          cid!,
                          orgType,
                          cluster?.deployType
                        ),
                        orgId: orgId!,
                        clusterId: cid!,
                        start: startTimeSec,
                        end: endTimeSec,
                        step: stepSec.toString() + 's',
                        isCloud
                      }).then((res) => res.data)
                    }}
                  />
                </div>
              )}
            />
          </td>
        </tr>
      )}
    </>
  )
}

interface RelatedMetricsProps {
  name: string
  charts: BenchmarkReportChart[]
  promqlSchema: PromqlSchemaMap
  reportArgs: ReportArgs
  cluster?: ClusterCluster
}

const RelatedMetrics: React.FC<RelatedMetricsProps> = ({
  name,
  charts,
  promqlSchema,
  reportArgs,
  cluster
}) => {
  const { t } = useTranslation(BENCHMARK_REPORT_NS)
  const { orgId, cid } = useParams<'orgId' | 'cid' | 'rid'>()
  const { curOrg } = useCurOrg()
  const { isCloud, orgType } = curOrg!

  return (
    <Collapse
      ghost
      onChange={(v) =>
        benchmarkReportTelemetry.toggleRelatedMetrics(name, !!v.length)
      }
    >
      <Collapse.Panel
        style={{ marginBottom: '-1rem' }}
        key='others'
        header={t('metric.related')}
      >
        <SingleOrMultiChart
          charts={charts}
          render={(chart) => {
            const queries = toMetricChartSchema(chart.queries, promqlSchema)
            return (
              <div>
                <h4 className='mb-0 ml-4'>
                  {chart.name}{' '}
                  <GrafanaLink
                    dashboards={queries as MetricDashboard[]}
                    orgId={curOrg?.id!}
                    orgType={curOrg?.orgType!}
                    clusterId={cluster?.id!}
                    reportArgs={reportArgs}
                  />
                </h4>
                <MetricsChart
                  unit={chart.unit}
                  queries={queries}
                  nullValue={TransformNullValue.AS_ZERO}
                  range={[reportArgs.begin_ts, reportArgs.end_ts]}
                  fetchPromeData={({
                    endTimeSec,
                    query,
                    startTimeSec,
                    stepSec
                  }) => {
                    return grafanaQueryRange({
                      query: replaceQuery(
                        query,
                        cid!,
                        orgType,
                        cluster?.deployType
                      ),
                      orgId: orgId!,
                      clusterId: cid!,
                      start: startTimeSec,
                      end: endTimeSec,
                      step: stepSec.toString() + 's',
                      isCloud
                    }).then((res) => res.data)
                  }}
                />
              </div>
            )
          }}
        />
      </Collapse.Panel>
    </Collapse>
  )
}

interface SingleOrMultiChartProps {
  charts: BenchmarkReportChart[]
  render: (chart: BenchmarkReportChart) => JSX.Element
}

const SingleOrMultiChart: React.FC<SingleOrMultiChartProps> = ({
  charts,
  render
}) => {
  if (charts.length === 1) {
    return render(charts[0])
  }

  return (
    <div className='flex flex-wrap'>
      {charts.map((chart) => (
        <div className='basis-1/2 p-2' key={chart.name}>
          {render(chart)}
        </div>
      ))}
    </div>
  )
}
